import { useState, useEffect, useCallback } from "react";
import type { AuthUser, AuthStatus } from "../types/index.js";
import { apiGet, apiPost } from "../lib/api.js";
import { API_BASE_URL } from "../config.js";

interface UseAuthReturn {
	user: AuthUser | null;
	status: AuthStatus;
	authError: string | null;
	login: () => void;
	logout: () => Promise<void>;
}

/**
 * Authentication hook.
 *
 * On mount:
 * 1. Checks for `?auth_error=` in the URL query string (set by the OAuth callback on failure).
 * 2. Calls GET /api/auth/me to check if the user has a valid session.
 *
 * Provides `login()` to redirect to Zoho OAuth and `logout()` to clear the session.
 */
export function useAuth(): UseAuthReturn {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [status, setStatus] = useState<AuthStatus>("loading");
	const [authError, setAuthError] = useState<string | null>(null);

	// Check for auth errors in the URL (set by OAuth callback redirect)
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const error = params.get("auth_error");

		if (error) {
			setAuthError(error);
			// Clean the URL so the error doesn't persist across refreshes
			const cleanUrl = window.location.pathname + window.location.hash;
			window.history.replaceState(null, "", cleanUrl);
		}
	}, []);

	// Check current session on mount
	useEffect(() => {
		let cancelled = false;

		async function checkSession(): Promise<void> {
			try {
				const response = await apiGet("/api/auth/me");

				if (cancelled) return;

				if (response.ok) {
					let data: { user?: AuthUser; scopesValid?: boolean };
					try {
						data = await response.json() as { user?: AuthUser; scopesValid?: boolean };
					} catch (parseErr) {
						console.error("[useAuth] /api/auth/me returned 200 but body is not valid JSON:", parseErr);
						setUser(null);
						setAuthError("server_error");
						setStatus("error");
						return;
					}

					if (!data.user) {
						console.warn("[useAuth] /api/auth/me returned 200 but response has no user object");
						setUser(null);
						setAuthError("server_error");
						setStatus("error");
						return;
					}

					setUser({ ...data.user, scopesValid: data.scopesValid });
					setStatus("authenticated");
				} else if (response.status === 401) {
					setUser(null);
					setStatus("unauthenticated");
				} else {
					console.warn(`[useAuth] /api/auth/me returned unexpected status ${response.status}`);
					setUser(null);
					setAuthError("server_error");
					setStatus("error");
				}
			} catch (err) {
				console.error("[useAuth] Failed to check session:", err);
				if (!cancelled) {
					setUser(null);
					setAuthError("server_error");
					setStatus("error");
				}
			}
		}

		checkSession();

		return () => {
			cancelled = true;
		};
	}, []);

	const login = useCallback(() => {
		window.location.href = `${API_BASE_URL}/api/auth/login`;
	}, []);

	const logout = useCallback(async () => {
		try {
			await apiPost("/api/auth/logout", {});
		} catch {
			// Best-effort — clear local state regardless
		}

		setUser(null);
		setStatus("unauthenticated");
	}, []);

	return { user, status, authError, login, logout };
}
